import { Title } from '@solidjs/meta'
import { createSignal } from 'solid-js'
import Badge from '~/components/Badge'
import Button from '~/components/Button'
import Card from '~/components/Card'
import Container from '~/components/Container'
import Icon from '~/components/Icon'
import Menu from '~/components/Menu'

export default function MenuPage() {
  const [selectedAction, setSelectedAction] = createSignal<string | null>(null)

  const demoItems = [
    { label: 'Editar Perfil', icon: 'material-symbols-light:edit-square-outline', onClick: () => setSelectedAction('Edit') },
    { label: 'Configuración', icon: 'material-symbols-light:settings-outline', onClick: () => setSelectedAction('Settings') },
    {
      label: 'Cerrar Sesión',
      icon: 'material-symbols-light:logout',
      onClick: () => setSelectedAction('Logout'),
      color: 'danger' as const
    }
  ]

  return (
    <>
      <Title>Menu - Florez Jaramillo Solutions</Title>

      <Container class="py-8">
        <div class="mb-10 mt-6 text-balance">
          <h1 class="text-4xl font-bold text-foreground mb-4">Menu</h1>
          <p class="text-foreground-subtle text-xl max-w-3xl leading-relaxed">
            Sistemas de acciones contextuales y menús desplegables. El componente Menu optimiza el espacio en pantalla ocultando acciones secundarias bajo triggers intuitivos, con soporte para clicks
            contextuales y navegación por teclado.
          </p>
        </div>

        <div class="space-y-12">
          {/* Menu de Acciones Estándar */}
          <Card>
            <Card.Header
              title="Interface de Acciones"
              subtitle="Despliega una lista de comandos con soporte para iconos y semántica de color."
              icon={<Icon icon="material-symbols-light:more-horiz" class="text-primary-500" size={24} />}
            />
            <Card.Body>
              <div class="flex flex-col items-center justify-center p-12 bg-surface-muted rounded-3xl border border-border border-dashed">
                <div class="flex items-center gap-6">
                  <Menu
                    trigger={
                      <Button variant="contained" size="lg" color="primary" icon="material-symbols-light:menu">
                        Opciones Avanzadas
                      </Button>
                    }
                    items={demoItems}
                  />

                  <div class="h-12 w-px bg-surface-muted hidden md:block" />

                  <div class="hidden md:flex flex-col gap-1">
                    <span class="text-[10px] font-black text-foreground-subtle uppercase tracking-widest">Estado del Buffer</span>
                    <div class="flex items-center gap-2">
                      <Badge color={selectedAction() ? 'success' : 'info'} size="sm" dot />
                      <span class="text-sm font-mono">{selectedAction() ? `CMD::${selectedAction()}` : 'IDLE_WAITING'}</span>
                    </div>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Variantes Contextuales y Compactas */}
          <Card>
            <Card.Header
              title="Versatilidad de Activación"
              subtitle="Desde botones discretos hasta zonas de interacción con click derecho (Context Menu)."
              icon={<Icon icon="material-symbols-light:mouse-outline" class="text-secondary-500" size={24} />}
            />
            <Card.Body>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-10">
                <div class="space-y-6">
                  <h4 class="text-sm font-bold text-foreground-subtle uppercase flex items-center gap-2">
                    <Icon icon="material-symbols-light:mouse-outline" size={18} /> Contextual (Right Click)
                  </h4>
                  <Menu
                    contextual
                    items={demoItems}
                    trigger={
                      <div class="h-40 w-full rounded-2xl bg-surface border-2 border-dashed border-border flex items-center justify-center group hover:border-primary-500 transition-colors cursor-context-menu">
                        <div class="text-center group-hover:scale-110 transition-transform">
                          <Icon icon="material-symbols-light:description-outline" size={48} class="mx-auto text-slate-300 group-hover:text-primary-500" />
                          <p class="text-xs text-foreground-subtle mt-2 font-bold tracking-tighter uppercase">Click derecho aquí</p>
                        </div>
                      </div>
                    }
                  />
                </div>

                <div class="space-y-6">
                  <h4 class="text-sm font-bold text-foreground-subtle uppercase flex items-center gap-2">
                    <Icon icon="material-symbols-light:search" size={18} /> Menu Compacto
                  </h4>
                  <div class="p-8 bg-surface rounded-2xl shadow-sm border border-border flex items-center justify-center gap-4">
                    <span class="text-sm text-foreground-subtle">Acciones rápidas:</span>
                    <Menu
                      trigger={<Button variant="soft" size="sm" icon="material-symbols-light:more-horiz" />}
                      items={[
                        { icon: 'material-symbols-light:content-copy-outline', tooltip: 'Copiar', onClick: () => {} },
                        { icon: 'material-symbols-light:share-outline', tooltip: 'Compartir', onClick: () => {} },
                        { icon: 'material-symbols-light:delete-outline', tooltip: 'Eliminar', onClick: () => {}, color: 'danger' }
                      ]}
                    />
                  </div>
                  <p class="text-[10px] text-foreground-subtle leading-relaxed italic">Los menús compactos son ideales para filas de tablas o cabeceras de tarjetas donde el espacio es crítico.</p>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* API Reference */}
          <Card>
            <Card.Header
              title="API Reference"
              subtitle="Especificaciones de configuración para el componente Menu."
              icon={<Icon icon="material-symbols-light:code" class="text-foreground-subtle" size={24} />}
            />
            <Card.Body>
              <div class="overflow-x-auto rounded-xl border border-border">
                <table class="w-full text-sm text-left">
                  <thead class="text-xs uppercase bg-surface-muted text-foreground-subtle">
                    <tr>
                      <th class="px-6 py-4 border-b">Propiedad</th>
                      <th class="px-6 py-4 border-b">Tipo</th>
                      <th class="px-6 py-4 border-b">Default</th>
                      <th class="px-6 py-4 border-b">Descripción</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-border">
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold text-primary-600">trigger</td>
                      <td class="px-6 py-4 italic text-xs">JSX.Element</td>
                      <td class="px-6 py-4 font-mono">-</td>
                      <td class="px-6 py-4">Elemento de UI que dispara la apertura del menú.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">items</td>
                      <td class="px-6 py-4 italic text-xs">MenuItemType[]</td>
                      <td class="px-6 py-4 font-mono">[]</td>
                      <td class="px-6 py-4">Colección de opciones con su lógica asociada.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold text-secondary-600">contextual</td>
                      <td class="px-6 py-4 italic text-xs">boolean</td>
                      <td class="px-6 py-4 font-mono">false</td>
                      <td class="px-6 py-4 italic">Cambia la activación a click derecho (mouse2).</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">placement</td>
                      <td class="px-6 py-4 italic text-xs">PopperPlacement</td>
                      <td class="px-6 py-4 font-mono">'bottom-start'</td>
                      <td class="px-6 py-4">Ubicación relativa al trigger.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Card.Body>
          </Card>
        </div>
      </Container>
    </>
  )
}
